import React, { useState, useEffect, useCallback } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import Seo from "./components/Seo";
import LandingPage from "./LandingPage";
import PricingPage from "./PricingPage";
import Login from "./Login";
import Signup from "./Signup";
import Dashboard from "./Dashboard";
import Events from "./Events";
import EventDetails from "./EventDetails";
import FacialRecognition from "./FacialRecognition";
import ClientGallery from "./ClientGallery";
import PersonGallery from "./PersonGallery";
import SelectionGallery from "./SelectionGallery";
import EventGallery from "./EventGallery";
import EditProfile from "./EditProfile";
import AdminPanel from "./AdminPanel";
import Invoice from "./Invoice";
import Contract from "./Contract";
import ContractSign from "./ContractSign";
import Wallet from "./Wallet";
import Subscribe from "./Subscribe";
import Portfolio from "./Portfolio";
import PortfolioPublic from "./PortfolioPublic";
import VideoPlayer from "./VideoPlayer";
import LiveSlideshow from "./LiveSlideshow";
import AdBanners from "./AdBanners";
import QrSharing from "./QrSharing";
import QrGalleryDetails from "./QrGalleryDetails";
import QrPublicGallery from "./QrPublicGallery";
import PhotoFrameOrder from "./PhotoFrameOrder";
import FrameOrderStatus from "./FrameOrderStatus";
import PdfReview from "./PdfReview";
import PdfFeedbackPage from "./PdfFeedbackPage";
import PhotentraAI from "./PhotentraAI";
import Reminders from "./Reminders";
import Team from "./Team";
import MyWebsite from "./MyWebsite";
import PhotoFrameSettings from "./PhotoFrameSettings";
import FrameOrders from "./FrameOrders";
import ForgotPassword from "./ForgotPassword";
import ResetPassword from "./ResetPassword";
import AuthCallback from "./AuthCallback";
import ShanBabe from "./ShanBabe";
import UploadLinkPage from "./UploadLinkPage";
import PrivacyPolicy from "./PrivacyPolicy";
import TermsOfService from "./TermsOfService";
import PaymentLinkPage from "./PaymentLinkPage";
import CrmDashboard from "./crm/CrmDashboard";
import CrmLeads from "./crm/CrmLeads";
import CrmClients from "./crm/CrmClients";
import CrmFollowUps from "./crm/CrmFollowUps";
import CrmQuotes from "./crm/CrmQuotes";
import CrmPackages from "./crm/CrmPackages";
import CrmCalendar from "./crm/CrmCalendar";
import CrmReports from "./crm/CrmReports";
import CrmTemplates from "./crm/CrmTemplates";
import CrmCampaigns from "./crm/CrmCampaigns";
import CrmEnquiryForm from "./crm/CrmEnquiryForm";
import EnquiryPublic from "./crm/EnquiryPublic";
import QuoteView from "./crm/QuoteView";
import { UploadManagerProvider } from "./components/UploadManager";
import DeepLinkListener from "./desktop/DeepLinkListener";
import DesktopSyncBridge from "./desktop/DesktopSyncBridge";
import DesktopNotifications from "./desktop/DesktopNotifications";
import UpdateBanner from "./desktop/UpdateBanner";
import LiveSyncPage from "./desktop/LiveSyncPage";
import UploadWidget from "./components/UploadWidget";
import ErrorBoundary from "./components/ErrorBoundary";
import "./App.css";

// Protected Route Component - redirects to login if not authenticated
const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem("token");
  return token ? children : <Navigate to="/login" />;
};

// Super Admin Route - only photentra@gmail.com can access
const AdminRoute = ({ children }) => {
  const token = localStorage.getItem("token");
  const role = localStorage.getItem("userRole");
  if (!token) return <Navigate to="/login" />;
  if (role !== "super_admin") return <Navigate to="/dashboard" />;
  return children;
};

// Studio Owner Route - team-member logins (role "assistant") can't manage the team,
// billing, or other owner-only surfaces, so this keeps them off /team entirely.
const OwnerRoute = ({ children }) => {
  const token = localStorage.getItem("token");
  const role = localStorage.getItem("userRole");
  if (!token) return <Navigate to="/login" />;
  if (role === "assistant") return <Navigate to="/dashboard" />;
  return children;
};

// Guest Route Component - redirects to dashboard if already logged in
const GuestRoute = ({ children }) => {
  const token = localStorage.getItem("token");
  return token ? <Navigate to="/dashboard" /> : children;
};

// Impersonation Banner — shown when admin is logged in as a studio user
function ImpersonationBanner() {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [studioEmail, setStudioEmail] = useState("");

  const checkImpersonation = useCallback(() => {
    const adminId = localStorage.getItem("admin_userId");
    setVisible(!!adminId);
    if (adminId) {
      setStudioEmail(localStorage.getItem("userEmail") || "");
    }
  }, []);

  useEffect(() => {
    checkImpersonation();
    // Listen for storage changes (in case impersonation starts/ends)
    window.addEventListener("storage", checkImpersonation);
    // Also poll briefly since same-tab storage events don't fire
    const interval = setInterval(checkImpersonation, 500);
    return () => {
      window.removeEventListener("storage", checkImpersonation);
      clearInterval(interval);
    };
  }, [checkImpersonation]);

  // Add top padding to body when impersonating so content isn't hidden
  useEffect(() => {
    if (visible) {
      document.body.style.paddingTop = "44px";
    } else {
      document.body.style.paddingTop = "";
    }
  }, [visible]);

  const switchBackToAdmin = () => {
    // Restore admin credentials
    localStorage.setItem("token", localStorage.getItem("admin_token"));
    localStorage.setItem("userId", localStorage.getItem("admin_userId"));
    localStorage.setItem("userEmail", localStorage.getItem("admin_userEmail"));
    localStorage.setItem("userRole", localStorage.getItem("admin_userRole"));
    // Clear impersonation keys
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_userId");
    localStorage.removeItem("admin_userEmail");
    localStorage.removeItem("admin_userRole");
    localStorage.removeItem("studioSlug");
    navigate("/admin");
  };

  if (!visible) return null;

  return (
    <div className="impersonation-bar">
      <span>🔑 Viewing as <strong>{studioEmail}</strong></span>
      <button className="impersonation-bar__btn" onClick={switchBackToAdmin}>
        ← Switch back to Admin
      </button>
    </div>
  );
}

const API_BASE_URL = process.env.REACT_APP_API_URL || "";

// Global subscription status overlay — shows expiry warnings and full lock screen.
// Fetches /api/users/:id/storage-usage on mount and every 5 minutes.
// Locked state (30+ days after expiry): full-screen blocking modal with Renew button.
// Grace/warning state: dismissible top banner.
function SubscriptionOverlay() {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  useEffect(() => {
    setBannerDismissed(false); // reset dismiss on page change
  }, [location.pathname]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("userId");
    if (!token || !userId) return;

    const check = () => {
      fetch(`${API_BASE_URL}/api/users/${userId}/storage-usage`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((r) => r.json())
        .then((data) => setStatus(data))
        .catch(() => {});
    };

    check();
    const interval = setInterval(check, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  if (!status) return null;

  // Don't show the lock overlay on the subscribe/wallet pages — user must be able to pay
  const isPayPath = location.pathname.startsWith("/subscribe") || location.pathname.startsWith("/wallet");

  if (status.is_locked && !isPayPath) {
    const planLabel = status.subscription_type
      ? status.subscription_type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
      : "your plan";
    return (
      <div style={{
        position: "fixed", inset: 0, zIndex: 99999,
        background: "rgba(0,0,0,0.82)", backdropFilter: "blur(6px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "24px",
      }}>
        <div style={{
          background: "#fff", borderRadius: 16, padding: "40px 36px", maxWidth: 440, width: "100%",
          textAlign: "center", boxShadow: "0 24px 80px rgba(0,0,0,0.4)",
        }}>
          <div style={{ fontSize: 52, marginBottom: 16 }}>🔒</div>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: "#111", margin: "0 0 10px" }}>
            Account Locked
          </h2>
          <p style={{ fontSize: 15, color: "#555", lineHeight: 1.6, margin: "0 0 8px" }}>
            Your <strong>{planLabel}</strong> subscription expired over 30 days ago.
          </p>
          <p style={{ fontSize: 14, color: "#777", lineHeight: 1.6, margin: "0 0 28px" }}>
            Your files and events are safe. Renew now to restore full access instantly.
          </p>
          <button
            onClick={() => navigate("/subscribe/pro_max_ultra")}
            style={{
              width: "100%", padding: "14px 0", borderRadius: 10,
              background: "#f97316", color: "#fff", fontWeight: 700, fontSize: 16,
              border: "none", cursor: "pointer", marginBottom: 12,
            }}
          >
            Renew Now
          </button>
          <button
            onClick={() => navigate("/wallet")}
            style={{
              width: "100%", padding: "12px 0", borderRadius: 10,
              background: "#f3f4f6", color: "#374151", fontWeight: 600, fontSize: 14,
              border: "none", cursor: "pointer",
            }}
          >
            Go to Wallet
          </button>
        </div>
      </div>
    );
  }

  if (bannerDismissed) return null;

  let bannerConfig = null;

  if (status.in_grace_period) {
    bannerConfig = {
      bg: "#fff7ed", border: "#fed7aa", left: "#f97316", icon: "⏳",
      title: `Subscription expired — ${status.grace_days_remaining} day${status.grace_days_remaining !== 1 ? "s" : ""} left to renew`,
      body: `Your files and events are safe. New uploads and events are paused. After ${status.grace_days_remaining} day${status.grace_days_remaining !== 1 ? "s" : ""} your account will be locked.`,
    };
  } else if (status.warn_days_remaining > 0) {
    const isYearly = status.billing_cycle === "yearly";
    bannerConfig = {
      bg: "#fefce8", border: "#fde68a", left: "#eab308", icon: "⚠️",
      title: `Subscription renews in ${status.warn_days_remaining} day${status.warn_days_remaining !== 1 ? "s" : ""}`,
      body: isYearly
        ? `Your annual plan is entering its final month. Renew before it expires to avoid a 30-day grace window.`
        : `Your monthly plan expires soon. Renew now to keep all features active without interruption.`,
    };
  }

  if (!bannerConfig) return null;

  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 9000,
      background: bannerConfig.bg,
      borderBottom: `1px solid ${bannerConfig.border}`,
      borderLeft: `4px solid ${bannerConfig.left}`,
      padding: "10px 16px",
      display: "flex", alignItems: "center", gap: 12,
    }}>
      <span style={{ fontSize: 18, flexShrink: 0 }}>{bannerConfig.icon}</span>
      <div style={{ flex: 1 }}>
        <span style={{ fontWeight: 700, fontSize: 13, color: "#111" }}>{bannerConfig.title} — </span>
        <span style={{ fontSize: 13, color: "#444" }}>{bannerConfig.body}</span>
      </div>
      <button
        onClick={() => navigate("/subscribe/pro_max_ultra")}
        style={{
          padding: "6px 14px", borderRadius: 6, background: bannerConfig.left,
          color: "#fff", fontWeight: 600, fontSize: 13, border: "none", cursor: "pointer", flexShrink: 0,
        }}
      >
        Renew
      </button>
      <button
        onClick={() => setBannerDismissed(true)}
        style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: "#999", flexShrink: 0, lineHeight: 1 }}
      >
        ×
      </button>
    </div>
  );
}

// A crash inside one screen must not blank the whole app. Keying the boundary on
// the path means navigating away clears the error instead of stranding the user.
function RoutedErrorBoundary({ children }) {
  const location = useLocation();
  return (
    <ErrorBoundary key={location.pathname} label={location.pathname}>
      {children}
    </ErrorBoundary>
  );
}

function App() {
  return (
    <Router>
      <UploadManagerProvider>
      <DeepLinkListener />
      <DesktopSyncBridge />
      <DesktopNotifications />
      <UpdateBanner />
      <ImpersonationBanner />
      <SubscriptionOverlay />
      {/* Rewrites title/description/canonical/robots on every client-side
          navigation, so the head does not stay stuck on whatever the server
          injected for the first URL loaded. */}
      <Seo />
      <RoutedErrorBoundary>
      <Routes>
        <Route path="/login" element={<GuestRoute><Login /></GuestRoute>} />
        <Route path="/signup" element={<GuestRoute><Signup /></GuestRoute>} />
        {/* Not a GuestRoute: it has to run even when a stale token is still in
            localStorage, otherwise the fresh social session would be discarded. */}
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/forgot-password" element={<GuestRoute><ForgotPassword /></GuestRoute>} />
        <Route path="/reset-password/:token" element={<GuestRoute><ResetPassword /></GuestRoute>} />
        <Route path="/shared/:token" element={<ClientGallery />} />
        <Route path="/shared/:token/person/:clusterId" element={<PersonGallery />} />
        <Route path="/shared/:token/selection" element={<SelectionGallery />} />
        <Route path="/shared/:token/gallery" element={<EventGallery />} />
        {/* Co-worker upload link (PIN-gated, upload-only). Public on purpose:
            works whether or not a studio is logged in on this browser. */}
        <Route path="/upload/:token" element={<UploadLinkPage />} />
        <Route path="/qr/:token" element={<QrPublicGallery />} />
        <Route path="/qr/:token/results/:resultToken" element={<QrPublicGallery />} />
        <Route path="/frame-order/:token/:imageId" element={<PhotoFrameOrder />} />
        <Route path="/frame-order/status/:trackingToken" element={<FrameOrderStatus />} />
        <Route path="/portfolio/:token" element={<PortfolioPublic />} />
        <Route path="/v/:token" element={<VideoPlayer />} />
        <Route path="/shan-anusha-babe" element={<ShanBabe />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/shared/:token/pdf-review" element={<PdfReview />} />
        <Route path="/event/:eventId/pdf-feedback/:submissionId" element={<ProtectedRoute><PdfFeedbackPage /></ProtectedRoute>} />
        <Route
          path="/portfolio"
          element={
            <ProtectedRoute>
              <Portfolio />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/events"
          element={
            <ProtectedRoute>
              <Events />
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm"
          element={
            <ProtectedRoute>
              <CrmDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/leads"
          element={
            <ProtectedRoute>
              <CrmLeads />
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/clients"
          element={
            <ProtectedRoute>
              <CrmClients />
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/followups"
          element={
            <ProtectedRoute>
              <CrmFollowUps />
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/quotes"
          element={
            <ProtectedRoute>
              <CrmQuotes />
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/packages"
          element={
            <ProtectedRoute>
              <CrmPackages />
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/calendar"
          element={
            <ProtectedRoute>
              <CrmCalendar />
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/reports"
          element={
            <ProtectedRoute>
              <CrmReports />
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/campaigns"
          element={
            <ProtectedRoute>
              <CrmCampaigns />
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/templates"
          element={
            <ProtectedRoute>
              <CrmTemplates />
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/enquiry-form"
          element={
            <ProtectedRoute>
              <CrmEnquiryForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="/live-sync"
          element={
            <ProtectedRoute>
              <LiveSyncPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/events/:eventId"
          element={
            <ProtectedRoute>
              <EventDetails />
            </ProtectedRoute>
          }
        />
        <Route
          path="/events/:eventId/invoice"
          element={
            <ProtectedRoute>
              <Invoice />
            </ProtectedRoute>
          }
        />
        <Route
          path="/events/:eventId/contract"
          element={
            <ProtectedRoute>
              <Contract />
            </ProtectedRoute>
          }
        />
        <Route path="/shared/:token/contract" element={<ContractSign />} />
        <Route path="/shared/:token/slideshow" element={<LiveSlideshow />} />
        <Route
          path="/wallet"
          element={
            <ProtectedRoute>
              <Wallet />
            </ProtectedRoute>
          }
        />
        <Route
          path="/subscribe/:planSlug"
          element={
            <ProtectedRoute>
              <Subscribe />
            </ProtectedRoute>
          }
        />
        <Route
          path="/facial-recognition"
          element={
            <ProtectedRoute>
              <FacialRecognition />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <EditProfile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/qr-sharing"
          element={
            <ProtectedRoute>
              <QrSharing />
            </ProtectedRoute>
          }
        />
        <Route
          path="/qr-sharing/:galleryId"
          element={
            <ProtectedRoute>
              <QrGalleryDetails />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ai"
          element={
            <ProtectedRoute>
              <PhotentraAI />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reminders"
          element={
            <ProtectedRoute>
              <Reminders />
            </ProtectedRoute>
          }
        />
        <Route
          path="/team"
          element={
            <OwnerRoute>
              <Team />
            </OwnerRoute>
          }
        />
        <Route
          path="/ad-banners"
          element={
            <ProtectedRoute>
              <AdBanners />
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-website"
          element={
            <ProtectedRoute>
              <MyWebsite />
            </ProtectedRoute>
          }
        />
        <Route
          path="/photo-frames"
          element={
            <ProtectedRoute>
              <PhotoFrameSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/photo-frame-orders"
          element={
            <ProtectedRoute>
              <FrameOrders />
            </ProtectedRoute>
          }
        />
        <Route path="/pay/:token" element={<PaymentLinkPage />} />
        <Route path="/quote/:token" element={<QuoteView />} />
        <Route path="/enquire/:token" element={<EnquiryPublic />} />
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <AdminPanel />
            </AdminRoute>
          }
        />
        <Route path="/pricing" element={<PricingPage />} />
        {/* No matching route used to render an empty page with no way out —
            especially easy to hit in the desktop app via a stale deep link. */}
        <Route
          path="*"
          element={<Navigate to={localStorage.getItem("token") ? "/dashboard" : "/"} replace />}
        />
        <Route
          path="/"
          element={
            localStorage.getItem("token") ? (
              <Navigate to="/dashboard" />
            ) : window.photentra?.isDesktop ? (
              // The landing page is a marketing site for people deciding whether
              // to sign up. Someone who has already installed the app has
              // decided — they want to sign in, not read a pitch.
              <Navigate to="/login" replace />
            ) : (
              <LandingPage />
            )
          }
        />
      </Routes>
      </RoutedErrorBoundary>
      <UploadWidget />
      </UploadManagerProvider>
    </Router>
  );
}

export default App;